import {
  INSURANCE_COVERAGE_CHECK_COUNTRIES,
  INSURANCE_COVERAGE_CHECK_PLAN_TYPES,
  InsuranceCoverageCheckCountry,
  InsuranceCoverageCheckEntityKind,
  InsuranceCoverageCheckInsuranceType,
  InsuranceCoverageCheckPlanType,
  SaveInsuranceCoverageCheckData,
} from 'hydra-data-types';
import { isEmpty, isNonEmptyString, stripUndefined } from 'ts-utils';
import type {
  CoverageCheckConsentValues,
  CoverageCheckLayoutConfig,
} from '../components/flows/layouts/CoverageCheckConsents';
import {
  COVERAGE_PLAN_TYPE_EMPLOYER,
  COVERAGE_PLAN_TYPE_MEDICAID,
  COVERAGE_PLAN_TYPE_SELF_PURCHASED,
  HEALTH_COVER_MEDICARE_ADVANTAGE,
  HEALTH_COVER_MEDICARE_ORIGINAL,
  NOT_LISTED_OPTION,
} from '../config/InsuranceProviders';
import { NervaControlScreenName } from '../config/nerva/NervaControlScreenNames';
import { StoreStateInternal } from '../store/Store';
import { UserDataState } from '../store/userData/UserDataState';
import { getCoverageAddress } from './CoverageAddress';
import { isAuCoverageAudience, isUsNon65Plus } from './MedicareAccess';
import { getPartnerInsurer, getPartnerInsurerAccessWithdrawal } from './PartnerInsurers';

const YES = 'Yes';
const NO = 'No';
const DATE_YMD = /^\d{4}-\d{2}-\d{2}$/;

/** The "check my coverage" variant for the user's coverage audience, if they're in one. */
export function getCoverageCheck(
  state: StoreStateInternal,
  layout: { coverageCheck?: CoverageCheckLayoutConfig; auCoverageCheck?: CoverageCheckLayoutConfig }
): CoverageCheckLayoutConfig | undefined {
  if (isUsNon65Plus(state)) return layout.coverageCheck;
  if (isAuCoverageAudience(state)) return layout.auCoverageCheck;
  return undefined;
}

/**
 * What the user did on the insurance-provider screen: the primary button, the self-pay link, or the
 * AU "I don't have private health insurance" link.
 */
export type InsuranceProviderSubmission =
  | { action: 'submit'; provider: string; consents?: CoverageCheckConsentValues }
  | { action: 'selfPay'; provider?: string }
  | { action: 'noPrivateInsurance' };

/**
 * A partner insurer picked where the coverage check was offered (`consents` is set): its no-cost
 * access offer replaces the check.
 */
function isPartnerInsurerPick(submission: InsuranceProviderSubmission): boolean {
  return (
    submission.action === 'submit' &&
    submission.consents !== undefined &&
    getPartnerInsurer(submission.provider) !== undefined
  );
}

/**
 * What the insurance-provider screen saves. The coverage keys are read back by
 * `getCoverageEntryRoute`, so they decide whether the user goes on into the coverage screens.
 * `consents` is set when the coverage check was shown; the consents it asks for gate the button, so a
 * submit carries Yes for each. The exits withdraw any consent given earlier so a user who went back
 * to take one isn't filed for outreach, and every exit offered but not taken is answered No. A
 * partner insurer replaces the coverage check with its own offer, so it records only the exits, and
 * the offer records the choice; any other answer withdraws partner access taken earlier.
 */
export function getInsuranceProviderUserData(
  screenName: string,
  submission: InsuranceProviderSubmission,
  coverageCheck: CoverageCheckLayoutConfig | undefined,
  storedUserData: UserDataState
): UserDataState {
  const answers = getProviderScreenAnswers(screenName, submission, coverageCheck);
  return isPartnerInsurerPick(submission)
    ? answers
    : { ...answers, ...getPartnerInsurerAccessWithdrawal(storedUserData) };
}

function getProviderScreenAnswers(
  screenName: string,
  submission: InsuranceProviderSubmission,
  coverageCheck: CoverageCheckLayoutConfig | undefined
): UserDataState {
  const noPrivateInsuranceNo =
    coverageCheck?.noPrivateInsuranceTitle === undefined
      ? {}
      : { coverageNoPrivateInsurance: [NO] };
  if (submission.action === 'noPrivateInsurance') {
    return {
      coverageNoPrivateInsurance: [YES],
      coverageSelfPay: [NO],
      coverageCheckAuthorization: [NO],
    };
  }
  if (submission.action === 'selfPay') {
    const { provider } = submission;
    return {
      coverageSelfPay: [YES],
      coverageCheckAuthorization: [NO],
      ...noPrivateInsuranceNo,
      ...(provider === undefined || isEmpty(provider) ? {} : { [screenName]: [provider] }),
    };
  }
  const { provider, consents } = submission;
  if (consents === undefined) return { [screenName]: [provider] };
  if (isPartnerInsurerPick(submission)) {
    return { [screenName]: [provider], coverageSelfPay: [NO], ...noPrivateInsuranceNo };
  }
  const asksAssignment = coverageCheck?.assignmentOfBenefits !== undefined;
  return {
    [screenName]: [provider],
    coverageCheckAuthorization: [consents.authorization ? YES : NO],
    ...(asksAssignment
      ? { coverageAssignmentOfBenefits: [consents.assignmentOfBenefits ? YES : NO] }
      : {}),
    coverageSelfPay: [NO],
    ...noPrivateInsuranceNo,
  };
}

/** The stored date of birth when it is in the `YYYY-MM-DD` form the callable accepts. */
function getDateOfBirth(userData: UserDataState): string | undefined {
  const dob = userData.dateOfBirth?.[0];
  return dob !== undefined && DATE_YMD.test(dob) ? dob : undefined;
}

/** The self-pay exit or (AU) "I don't have private health insurance", chosen instead of the check. */
function hasTakenCoverageExit(userData: UserDataState): boolean {
  return userData.coverageSelfPay?.[0] === YES || userData.coverageNoPrivateInsurance?.[0] === YES;
}

/**
 * The coverage check is complete once the user authorised it, answered the address step (chose an
 * address or said they couldn't find it), and has a date of birth on file. Both flows ask date of
 * birth before the coverage screens, and the callable requires it alongside advocacy consent, so
 * without it the save would only be rejected. AU has no address step, so it files as soon as the
 * user consents; the optional membership number that follows then updates the same document.
 * Taking an exit rules it out even if an earlier consent is still stored, e.g. a user who consented,
 * went back and chose to self-pay. Picking a partner insurer rules it out the same way, since those
 * members are offered no-cost access instead. A check already filed by then stays filed.
 */
export function isCoverageCheckComplete(state: StoreStateInternal): boolean {
  const { userData } = state;
  return (
    userData.coverageCheckAuthorization?.[0] === YES &&
    !hasTakenCoverageExit(userData) &&
    getPartnerInsurer(getProviderAnswer(userData)) === undefined &&
    (isAuCoverageAudience(state) ||
      getCoverageAddress(userData) !== undefined ||
      userData.coverageAddressNotFound !== undefined) &&
    getDateOfBirth(userData) !== undefined
  );
}

export function getProviderAnswer(userData: UserDataState): string | undefined {
  const answers = userData as Partial<Record<string, string[]>>;
  return answers[NervaControlScreenName.InsuranceProvider]?.[0];
}

function isPlanType(value: string | undefined): value is InsuranceCoverageCheckPlanType {
  return INSURANCE_COVERAGE_CHECK_PLAN_TYPES.includes(value as InsuranceCoverageCheckPlanType);
}

/** An Original Medicare dropdown pick skips the plan-type screen, so it stands in for the answer. */
function getPlanType(
  userData: UserDataState,
  provider: string | undefined
): InsuranceCoverageCheckPlanType {
  const answered = userData.coveragePlanType?.[0];
  if (isPlanType(answered)) return answered;
  return provider === HEALTH_COVER_MEDICARE_ORIGINAL ? HEALTH_COVER_MEDICARE_ORIGINAL : '';
}

const PUBLIC_PROGRAMS: string[] = [
  HEALTH_COVER_MEDICARE_ORIGINAL,
  HEALTH_COVER_MEDICARE_ADVANTAGE,
  COVERAGE_PLAN_TYPE_MEDICAID,
];

function getInsuranceType(
  planType: InsuranceCoverageCheckPlanType
): InsuranceCoverageCheckInsuranceType {
  if (planType === COVERAGE_PLAN_TYPE_EMPLOYER) return 'Employer-sponsored insurance';
  if (planType === COVERAGE_PLAN_TYPE_SELF_PURCHASED) return 'Private health insurance';
  if (PUBLIC_PROGRAMS.includes(planType)) return 'Public / government program';
  return '';
}

/** The payer entity the answers point at; the employer is the higher-value key for US advocacy. */
function getEntityKind(
  planType: InsuranceCoverageCheckPlanType,
  insurer: string
): InsuranceCoverageCheckEntityKind {
  if (planType === COVERAGE_PLAN_TYPE_EMPLOYER) return 'employer';
  if (PUBLIC_PROGRAMS.includes(planType)) return 'program';
  return insurer === '' ? '' : 'insurer';
}

function getCountry(country: string): InsuranceCoverageCheckCountry {
  return INSURANCE_COVERAGE_CHECK_COUNTRIES.includes(country as InsuranceCoverageCheckCountry)
    ? (country as InsuranceCoverageCheckCountry)
    : 'OTHER';
}

function consentGiven(answer: string[] | undefined): boolean | undefined {
  return answer === undefined ? undefined : answer[0] === YES;
}

/**
 * Maps the coverage-flow answers and account details onto the `/insurance_coverage_check` document
 * the website form also writes. Returns `undefined` when the flow isn't complete or there is no
 * account email to file it under. `id` is set once a document exists so the save updates it.
 * `sourcePath` is the flow config name (e.g. NervaControl), passed in so this module doesn't import
 * the flow configs.
 */
export function buildInsuranceCoverageCheck(
  state: StoreStateInternal,
  sourcePath: string
): SaveInsuranceCoverageCheckData | undefined {
  const { user, userData, entry } = state;
  if (!isCoverageCheckComplete(state) || !isNonEmptyString(user.email)) return undefined;

  const provider = getProviderAnswer(userData);
  // "Not listed" carries no payer; a curated dropdown pick counts as verified.
  const insurer = provider === undefined || provider === NOT_LISTED_OPTION ? '' : provider;
  const planType = getPlanType(userData, provider);
  // Only email accounts carry a last name; token users have just `name`.
  const lastName = 'lastName' in user ? user.lastName : undefined;

  const check: SaveInsuranceCoverageCheckData = {
    id: userData.insuranceCoverageCheckId?.[0],
    source: 'webAssessment',
    sourcePath,
    country: getCountry(entry.country),
    insurer,
    employer: userData.coverageUsEmployer?.[0] ?? '',
    entityKind: getEntityKind(planType, insurer),
    entityVerified: insurer !== '',
    name: [user.name, lastName].filter(isNonEmptyString).join(' '),
    planType,
    // AU only asks for a private health insurer, and has no plan-type question.
    insuranceType: isAuCoverageAudience(state)
      ? 'Private health insurance'
      : getInsuranceType(planType),
    coverDetail: (userData.coverageBenefits ?? []).join(', '),
    email: user.email,
    // Only authorised users get this far, and the authorisation covers contacting the plan.
    consent: true,
    advocacyConsent: true,
    dob: getDateOfBirth(userData) ?? '',
    memberId: userData.coverageMemberId?.[0] ?? '',
    healthPlanConsent: consentGiven(userData.coverageCheckAuthorization),
    assignmentOfBenefitsConsent: consentGiven(userData.coverageAssignmentOfBenefits),
    employerOutreachConsent: consentGiven(userData.coverageEmployerOutreachConsent),
    address: getCoverageAddress(userData),
  };
  // The callable's schema rejects unknown keys; undefined optionals must not be sent as keys.
  return stripUndefined(check);
}
